import { lazy, Suspense, useEffect, useState } from 'react';
import { ApiError, getCurrentAdminUser, logoutAdmin } from '../lib/api';
import type { AdminUser } from '../lib/types';
import { AdminLayout } from './AdminLayout';
import { LoginPage } from './pages/LoginPage';

const AuditLogAdmin = lazy(() => import('./pages/AuditLogAdmin').then((module) => ({ default: module.AuditLogAdmin })));
const ContactSubmissionsAdmin = lazy(() => import('./pages/ContactSubmissionsAdmin').then((module) => ({ default: module.ContactSubmissionsAdmin })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const FilesAdmin = lazy(() => import('./pages/FilesAdmin').then((module) => ({ default: module.FilesAdmin })));
const GalleryImagesAdmin = lazy(() => import('./pages/GalleryImagesAdmin').then((module) => ({ default: module.GalleryImagesAdmin })));
const NavAdmin = lazy(() => import('./pages/NavAdmin').then((module) => ({ default: module.NavAdmin })));
const PagesAdmin = lazy(() => import('./pages/PagesAdmin').then((module) => ({ default: module.PagesAdmin })));
const ProfilesAdmin = lazy(() => import('./pages/ProfilesAdmin').then((module) => ({ default: module.ProfilesAdmin })));
const SecurityAdmin = lazy(() => import('./pages/SecurityAdmin').then((module) => ({ default: module.SecurityAdmin })));
const SocialLinksAdmin = lazy(() => import('./pages/SocialLinksAdmin').then((module) => ({ default: module.SocialLinksAdmin })));

export function AdminApp() {
  const path = window.location.pathname;
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(path !== '/admin/login');
  const [error, setError] = useState('');

  useEffect(() => {
    if (path === '/admin/login') {
      return;
    }

    let isCurrent = true;
    setIsLoading(true);
    getCurrentAdminUser()
      .then((currentUser) => {
        if (isCurrent) {
          setUser(currentUser);
          setError('');
        }
      })
      .catch((loadError) => {
        if (!isCurrent) {
          return;
        }

        if (loadError instanceof ApiError && loadError.status === 401) {
          setUser(null);
          setError('');
          return;
        }

        setError(loadError instanceof Error ? loadError.message : 'Unable to load admin session');
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [path]);

  async function handleLogout() {
    await logoutAdmin();
    setUser(null);
    window.history.pushState({}, '', '/admin/login');
  }

  function handleLogin(nextUser: AdminUser) {
    setUser(nextUser);
    window.history.pushState({}, '', '/admin');
  }

  if (path === '/admin/login' || (!isLoading && !user && !error)) {
    return <LoginPage onLogin={handleLogin} />;
  }

  if (isLoading) {
    return (
      <main className="admin-login-shell">
        <p>Loading admin session...</p>
      </main>
    );
  }

  if (error || !user) {
    return (
      <main className="admin-login-shell">
        <section className="admin-login-panel">
          <h1>Admin unavailable</h1>
          <p role="alert">{error || 'Authentication required'}</p>
          <a href="/admin/login">Return to sign in</a>
        </section>
      </main>
    );
  }

  return (
    <AdminLayout currentPath={path} user={user} onLogout={handleLogout}>
      {renderAdminRoute(path)}
    </AdminLayout>
  );
}

function renderAdminRoute(path: string) {
  let Screen = DashboardPage;

  if (path.startsWith('/admin/pages')) Screen = PagesAdmin;
  else if (path.startsWith('/admin/nav')) Screen = NavAdmin;
  else if (path.startsWith('/admin/profiles')) Screen = ProfilesAdmin;
  else if (path.startsWith('/admin/social')) Screen = SocialLinksAdmin;
  else if (path.startsWith('/admin/users')) Screen = SecurityAdmin;
  else if (path.startsWith('/admin/contact')) Screen = ContactSubmissionsAdmin;
  else if (path.startsWith('/admin/audit')) Screen = AuditLogAdmin;
  else if (path.startsWith('/admin/gallery')) Screen = GalleryImagesAdmin;
  else if (path.startsWith('/admin/files')) Screen = FilesAdmin;

  return (
    <Suspense fallback={<p>Loading admin screen...</p>}>
      <Screen />
    </Suspense>
  );
}
